import React from 'react';
import { Card, CardContent } from '@/components/ui/Card';
import { QuotationMetricsSummary } from '@/types/domain/quotation-comparison';
import { formatCurrency } from '@/lib/utils';
import { Layers, CheckCircle2, Clock, Sparkles, TrendingDown, FileText } from 'lucide-react';

interface QuotationKpiCardsProps {
  metrics: QuotationMetricsSummary;
}

export const QuotationKpiCards: React.FC<QuotationKpiCardsProps> = ({ metrics }) => {
  const cards = [
    {
      title: 'Total Bids Received',
      value: metrics.totalQuotations,
      icon: FileText,
      color: 'text-primary bg-primary/10 border-primary/20',
      description: 'Supplier responses received',
    },
    {
      title: 'Active RFQ Packages',
      value: metrics.activeRfqs,
      icon: Layers,
      color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      description: 'Sourcing packages in review',
    },
    {
      title: 'Evaluations In Progress',
      value: metrics.evaluationsInProgress,
      icon: Clock,
      color: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
      description: 'Pending supplier selection',
    },
    {
      title: 'Selected & Awarded',
      value: metrics.evaluationsCompleted,
      icon: CheckCircle2,
      color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      description: 'Sanctioned for PO release',
    },
    {
      title: 'Potential Savings',
      value: formatCurrency(metrics.totalSavings, 'INR'),
      icon: TrendingDown,
      color: 'text-emerald-600 bg-emerald-500/10 border-emerald-500/20',
      description: 'Savings vs average bid',
    },
    {
      title: 'AI Recommendations',
      value: metrics.aiRecommendationsCount,
      icon: Sparkles,
      color: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
      description: 'AI decision insights ready',
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3.5 font-poppins">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <Card key={c.title} className="border-border rounded-2xl shadow-2xs overflow-hidden">
            <CardContent className="p-3.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-extrabold text-foreground-secondary uppercase tracking-wider">
                  {c.title}
                </span>
                <div className={`p-2 rounded-xl border ${c.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
              </div>

              <div>
                <span className="text-xl font-extrabold text-foreground tracking-tight block">
                  {c.value}
                </span>
                <span className="text-[10px] font-bold text-foreground-muted block mt-0.5">
                  {c.description}
                </span>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};
